import React from 'react';
import { Button, Col, Form, Input, Space, Typography } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { applyGeneratedPassword, buildPasswordFormRules } from '../utils/passwordPolicyUtils';

const { Text } = Typography;

/**
 * 密碼輸入 + 自動產生按鈕；policy 由 usePasswordPolicy 載入後傳入。
 */
const PasswordFieldWithGenerate = ({
  form,
  scope,
  policy,
  generating = false,
  passwordName = 'password',
  confirmName = 'confirmPassword',
  passwordLabel,
  confirmLabel,
  passwordRequired = false,
  passwordRequiredMessage,
  confirmRequiredMessage,
  passwordMismatchMessage,
  passwordPlaceholder,
  confirmPlaceholder,
  skipPasswordValidationWhenEmpty = false,
  showConfirm = true,
  showPolicyHint = true,
  generateLabel = '自動產生',
  policyHint,
  passwordRulesExtra = [],
  confirmRulesExtra = [],
  useGridLayout = true,
}) => {
  const policyMessage = policyHint || policy?.message;
  const mismatchMessage = passwordMismatchMessage || confirmRequiredMessage;

  const passwordRules = [
    ...buildPasswordFormRules(policy, {
      required: passwordRequired,
      requiredMessage: passwordRequiredMessage,
      skipWhenEmpty: skipPasswordValidationWhenEmpty,
    }),
    ...passwordRulesExtra,
  ];

  const confirmRules = showConfirm
    ? [
        ({ getFieldValue }) => ({
          validator(_, value) {
            const password = getFieldValue(passwordName);
            if (skipPasswordValidationWhenEmpty && (!password || !String(password).trim())) {
              return Promise.resolve();
            }
            if (passwordRequired) {
              if (!password || !String(password).trim()) {
                return Promise.reject(new Error(passwordRequiredMessage));
              }
              if (!value || !String(value).trim()) {
                return Promise.reject(new Error(confirmRequiredMessage));
              }
              if (password !== value) {
                return Promise.reject(new Error(mismatchMessage));
              }
              return Promise.resolve();
            }
            if (!password || !String(password).trim()) {
              return Promise.resolve();
            }
            if (!value || !String(value).trim()) {
              return Promise.reject(new Error(confirmRequiredMessage));
            }
            if (password === value) {
              return Promise.resolve();
            }
            return Promise.reject(new Error(mismatchMessage));
          },
        }),
        ...confirmRulesExtra,
      ]
    : [];

  const handleGenerate = async () => {
    await applyGeneratedPassword(form, scope, {
      password: passwordName,
      confirmPassword: showConfirm ? confirmName : null,
    });
  };

  const passwordBlock = (
    <>
      <Form.Item label={passwordLabel} name={passwordName} rules={passwordRules}>
        <Space.Compact style={{ width: '100%' }}>
          <Input.Password
            autoComplete="new-password"
            placeholder={passwordPlaceholder}
            style={{ width: '100%' }}
          />
          <Button
            icon={<ThunderboltOutlined />}
            loading={generating}
            onClick={handleGenerate}
          >
            {generateLabel}
          </Button>
        </Space.Compact>
      </Form.Item>
      {showPolicyHint && policyMessage ? (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: -8, marginBottom: 12 }}>
          {policyMessage}
        </Text>
      ) : null}
    </>
  );

  const confirmBlock = showConfirm ? (
    <Form.Item
      label={confirmLabel}
      name={confirmName}
      dependencies={[passwordName]}
      rules={confirmRules}
    >
      <Input.Password autoComplete="new-password" placeholder={confirmPlaceholder} />
    </Form.Item>
  ) : null;

  if (!useGridLayout) {
    return (
      <>
        {passwordBlock}
        {confirmBlock}
      </>
    );
  }

  return (
    <>
      <Col span={showConfirm ? 12 : 24}>
        {passwordBlock}
      </Col>
      {showConfirm ? <Col span={12}>{confirmBlock}</Col> : null}
    </>
  );
};

export default PasswordFieldWithGenerate;
